/**
 * ConfirmDialog — Reusable confirmation modal replacing native window.confirm() / alert().
 *
 * @component
 * @props {boolean} open - Show/hide
 * @props {function} onConfirm - Called when user confirms
 * @props {function} onCancel - Called when user cancels (oder Modal closed bei infoOnly)
 * @props {string} title - Dialog title
 * @props {string} message - Dialog message
 * @props {string} [confirmLabel='OK'] - Confirm button text
 * @props {string} [cancelLabel='Abbrechen'] - Cancel button text
 * @props {'primary'|'danger'} [variant='primary'] - Button style
 * @props {boolean} [infoOnly=false] - Info-Modus: nur Confirm-Button (ersetzt window.alert())
 * @since v2.175.1, infoOnly seit v2.416.13 (#960)
 */
import { useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';

export default function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel,
  variant = 'primary',
  zIndex = 60,
  infoOnly = false,
}) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (open && confirmRef.current) confirmRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const isDanger = variant === 'danger';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', zIndex }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[480px] p-6"
        style={{ boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          {isDanger && (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-50 shrink-0">
              <Trash2 size={18} className="text-red-500" />
            </div>
          )}
          <div>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
            {message && <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{message}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          {!infoOnly && (
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-sm font-medium border"
              style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary, #E5E7EB)' }}
            >
              {cancelLabel || 'Abbrechen'}
            </button>
          )}
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: isDanger ? '#DC2626' : '#338edc' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
